
import moment from 'moment';

import {
  defineComponent,
  defineAsyncComponent,
  ref,
  reactive,
  onMounted,
} from 'vue';

import type {
  GenericObject,
  Endpoints,
  Countries,
  Platforms,
  Browsers,
  Statistics,
} from '@/types';

import {
  TRAFFIC_CATEGORIES,
  TRAFFIC_TYPES,
  TRAFFIC_SOURCE_RTB,
} from '@/libs/consts';

import useEntriesNext from '@/composable/useEntries@next';

import useTimezonePreference from '@/composable/useTimezonePreference';

import useSubStatisticsComponent from '@/composable/useSubStatisticsComponent';

export default defineComponent({
  components: {
    Actions: defineAsyncComponent(() => import('@/components/Statistics/RTB/Actions.vue')),
    ViewsColumn: defineAsyncComponent(() => import('@/components/Statistics/RTB/Columns/ViewsColumn.vue')),
    StatisticsByHours: defineAsyncComponent(() => import('@/components/Statistics/RTB/StatisticsByHours.vue')),
    StatisticsByEndpoints: defineAsyncComponent(() => import('@/components/Statistics/RTB/StatisticsByEndpoints.vue')),
    StatisticsBySubids: defineAsyncComponent(() => import('@/components/Statistics/RTB/StatisticsBySubids.vue')),
    StatisticsByCountries: defineAsyncComponent(() => import('@/components/Statistics/RTB/StatisticsByCountries.vue')),
    StatisticsByPlatforms: defineAsyncComponent(() => import('@/components/Statistics/RTB/StatisticsByPlatforms.vue')),
    StatisticsByBrowsers: defineAsyncComponent(() => import('@/components/Statistics/RTB/StatisticsByBrowsers.vue')),
  },
  setup() {
    const {
      entries: endpoints,
      entriesLoading: endpointsLoading,
      fetchEntries: fetchEndpoints,
    } = useEntriesNext<Endpoints>('/api/endpoint/getEndpoints');

    const {
      entries: countries,
      entriesLoading: countriesLoading,
      fetchEntries: fetchCountries,
    } = useEntriesNext<Countries>('/api/country/getCountries');

    const {
      entries: platforms,
      entriesLoading: platformsLoading,
      fetchEntries: fetchPlatforms,
    } = useEntriesNext<Platforms>('/api/platform/getPlatforms');

    const {
      entries: browsers,
      entriesLoading: browsersLoading,
      fetchEntries: fetchBrowsers,
    } = useEntriesNext<Browsers>('/api/browser/getBrowsers');

    const {
      entries: statistics,
      entriesLoading: statisticsLoading,
      entriesFetched: statisticsFetched,
      fetchEntries: fetchStatistics,
    } = useEntriesNext<Statistics>('/api/statistics/publisher/rtb/getStatisticsByDays');

    const { savedTimezone, watchTimezone } = useTimezonePreference();

    const {
      subStatisticsComponent,
      setSubStatisticsComponent,
      resetSubStatisticComponent,
    } = useSubStatisticsComponent();

    const getStatisticByDayRowClass = (row: GenericObject): string => (row.date === subStatisticsComponent.filters.date ? 'statistics-table-row-active' : '');

    const defaultStatisticsFilters = {
      date_from: moment().subtract(4, 'days').format('YYYY-MM-DD'),
      date_to: moment().format('YYYY-MM-DD'),
      timezone: savedTimezone,
      traffic_type: null,
      traffic_category: null,
      endpoint_id: null,
      country_id: null,
      platform_id: null,
      browser_id: null,
    };

    const statisticsFilters = reactive<GenericObject>({ ...defaultStatisticsFilters });

    watchTimezone(() => statisticsFilters.timezone);

    const statisticsFiltersKeyCounter = ref<number>(0);

    const resetStatisticsFilters = (): void => {
      Object.assign(statisticsFilters, defaultStatisticsFilters);

      statisticsFiltersKeyCounter.value += 1;
    };

    const fetchEndpointsWrapper = async (): Promise<void> => fetchEndpoints({
      traffic_source: TRAFFIC_SOURCE_RTB,
    });

    const fetchStatisticsWrapper = async (): Promise<void> => {
      fetchStatistics(statisticsFilters);

      resetSubStatisticComponent();
    };

    onMounted(() => Promise.all([
      fetchEndpointsWrapper(),
      fetchCountries(),
      fetchPlatforms(),
      fetchBrowsers(),
    ]));

    return {
      TRAFFIC_CATEGORIES,
      TRAFFIC_TYPES,
      endpoints,
      endpointsLoading,
      countries,
      countriesLoading,
      platforms,
      platformsLoading,
      browsers,
      browsersLoading,
      statistics,
      statisticsLoading,
      statisticsFetched,
      statisticsFilters,
      statisticsFiltersKeyCounter,
      resetStatisticsFilters,
      fetchStatisticsWrapper,
      subStatisticsComponent,
      setSubStatisticsComponent,
      getStatisticByDayRowClass,
    };
  },
});
